import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { WebSocketService } from './websocket.service';

function toFiniteNumber(value: any): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === 'string') {
		const parsed = Number.parseFloat(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

function formatDegrees(value: number, unit: 'C' | 'F'): string {
	return `${value}°${unit}`;
}

function directionFromDegrees(degrees: number): string {
	const normalized = ((degrees % 360) + 360) % 360;
	const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
	const index = Math.round(normalized / 45) % points.length;
	return points[index];
}

@Injectable({
	providedIn: 'root',
})
export class RaceInfoService {
	private raceSessionService = inject(RaceSessionService);
	private webSocketService = inject(WebSocketService);

	public raceStatus = toSignal(this.raceSessionService.raceStatus$);
	public timeStatus = toSignal(this.raceSessionService.timeStatus$);
	public weather = toSignal(this.webSocketService.weather$, { initialValue: {} as any });
	public localNow = signal(new Date());

	constructor() {
		setInterval(() => {
			this.localNow.set(new Date());
		}, 1000);
	}

	public trackTempDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;

		const trackTemp = toFiniteNumber(currentData?.trackTemperature ?? null);
		if (trackTemp !== null) {
			return formatDegrees(trackTemp, 'C');
		}

		return '--';
	});

	public ambientTempDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;

		const ambientTemp = toFiniteNumber(currentData?.ambientTemperature ?? null);
		if (ambientTemp !== null) {
			return formatDegrees(ambientTemp, 'C');
		}

		return '--';
	});

	public windSpeedDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;

		const windSpeed = toFiniteNumber(currentData?.windSpeed ?? null);
		if (windSpeed !== null) {
			return `${windSpeed} kph`;
		}

		return '--';
	});

	public windDirectionDegrees = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const degrees = toFiniteNumber(
			currentData?.windDirectionDegrees ??
				currentData?.windDirectionDeg ??
				currentData?.windDirection ??
				currentData?.windDirDegrees ??
				null,
		);

		if (degrees === null) {
			return 0;
		}

		return ((degrees % 360) + 360) % 360;
	});

	public windDirectionLabel = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const cardinals =
			currentData?.windDirectionCardinal ??
			currentData?.windDirectionText ??
			currentData?.windDirectionCompass ??
			null;

		if (cardinals !== null) {
			return String(cardinals);
		}

		const degrees = toFiniteNumber(
			currentData?.windDirectionDegrees ??
				currentData?.windDirectionDeg ??
				currentData?.windDirection ??
				currentData?.windDirDegrees ??
				null,
		);
		if (degrees === null) {
			return '--';
		}

		return directionFromDegrees(degrees);
	});

	public localTimeDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const streamLocalTime =
			currentData?.localTime ?? currentData?.localTimestamp ?? currentData?.sessionLocalTime ?? null;

		if (streamLocalTime !== null) {
			const timeString = String(streamLocalTime);
			const parsed = new Date(timeString);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed.toLocaleTimeString([], {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: true,
				});
			}
			return timeString;
		}

		return this.localNow().toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		});
	});
}
